import * as React from 'react';
import { IInvoiceBudgetBalanceDto } from 'api/odata/generated/entities/IInvoiceBudgetBalanceDto';
import { CellRenderer } from 'app/components/BasicTable/BasicTableProps';
import { Tooltip } from 'app/components/BasicTooltips/Tooltip';
import { translations } from 'locales/translations';
import { useTranslation } from 'react-i18next';
import { uniqueId } from 'lodash';
import { InvoiceDetailsSidePanelLink } from '../InvoiceDetails/InvoiceDetailsSidePanel';
import { toEntity } from 'utils/entity-utils';
import { AddChargeButton } from '../AddChargePage/AddChargeButton';
import { AddPaymentButton } from '../AddPaymentPage/AddPaymentButton';
import { IInvoiceBalanceDto } from 'api/odata/generated/complexTypes/IInvoiceBalanceDto';
import { dateUtils } from 'utils/date-utils';
import { useSelector } from 'react-redux';
import { selectglobalSettings } from 'app/slice/selectors';
import { AllowedSettings } from 'utils/globalSettings';
import { ReactActions } from 'app/components/ReactActions';
import { Icon } from 'app/components/BasicIcons/FontAwesome';
import { Box } from '@material-ui/core';

const tryParseInvoicesData = (s: string | null): IInvoiceBalanceDto[] => {
  try {
    const invoices = JSON.parse(s ?? '[]') as IInvoiceBalanceDto[];
    return invoices;
  } catch {
    return [] as IInvoiceBalanceDto[];
  }
};
export const useRowActions = () => {
  const { t } = useTranslation();
  const settings = useSelector(selectglobalSettings);
  const allowedFees = settings.GetString(AllowedSettings.AllowedFeeTypes);
  const accumulateInvoices = settings.GetBoolean(
    AllowedSettings[AllowedSettings.AccumulateNewChargesToExistingInvoice],
  );
  const isChargesAllowed = allowedFees && allowedFees.trim() !== '';
  const rowActions = React.useMemo<CellRenderer<IInvoiceBudgetBalanceDto>[]>(
    () => [
      ({ row }) => {
        const invoices = tryParseInvoicesData(row.original.Invoices);
        const actions: JSX.Element[] = [];
        if (invoices.length === 0) {
          // add charge/payment by service group & budget
          if (isChargesAllowed && accumulateInvoices) {
            actions.push(
              <AddChargeButton
                budget={getBudget(row.original)}
                serviceGroup={getServiceGroup(row.original)}
              >
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    gap: 16,
                  }}
                >
                  <Icon icon="plus" />
                  {t(translations.AddACharge)}
                </Box>
              </AddChargeButton>,
            );
          }
          if (accumulateInvoices) {
            actions.push(
              <AddPaymentButton
                budget={getBudget(row.original)}
                serviceGroup={getServiceGroup(row.original)}
              >
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    gap: 16,
                  }}
                >
                  <Icon icon="plus" />
                  {t(translations.AddPayment)}
                </Box>
              </AddPaymentButton>,
            );
          }
        } else if (invoices.length === 1) {
          // combine all differnet actions into single list for single invoice row
          actions.push(<InvoiceDetailsLink invoiceId={invoices[0].Id} />);
          if (isChargesAllowed && accumulateInvoices) {
            actions.push(
              <AddChargeButton
                invoiceId={invoices[0].Id ?? undefined}
                budget={getBudget(row.original)}
                serviceGroup={getServiceGroup(row.original)}
              >
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    gap: 16,
                  }}
                >
                  <Icon icon="plus" />
                  {t(translations.AddACharge)}
                </Box>
              </AddChargeButton>,
            );
          }
          if (accumulateInvoices) {
            actions.push(
              <AddPaymentButton
                amount={getPaymentAmount(invoices[0])}
                invoiceId={invoices[0].Id ?? undefined}
                budget={getBudget(row.original)}
                serviceGroup={getServiceGroup(row.original)}
              >
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    gap: 16,
                  }}
                >
                  <Icon icon="plus" />
                  {t(translations.AddPayment)}
                </Box>
              </AddPaymentButton>,
            );
          }
        } else if (invoices?.length ?? 0 > 1) {
          // show nested actions per each invoice row:
          // invoice label + all actions behind the "ReactActions"

          invoices.forEach(invoice => {
            let subActions: JSX.Element[] = [];
            subActions.push(<InvoiceDetailsLink invoiceId={invoice.Id} />);
            if (isChargesAllowed && accumulateInvoices) {
              subActions.push(
                <AddChargeButton
                  invoiceId={invoice.Id ?? undefined}
                  budget={getBudget(row.original)}
                  serviceGroup={getServiceGroup(row.original)}
                >
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                      gap: 16,
                    }}
                  >
                    <Icon icon="plus" />
                    {t(translations.AddACharge)}
                  </Box>
                </AddChargeButton>,
              );
            }
            if (accumulateInvoices) {
              subActions.push(
                <AddPaymentButton
                  amount={getPaymentAmount(invoice)}
                  invoiceId={invoice.Id ?? undefined}
                  budget={getBudget(row.original)}
                  serviceGroup={getServiceGroup(row.original)}
                >
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                      gap: 16,
                    }}
                  >
                    <Icon icon="plus" />
                    {t(translations.AddPayment)}
                  </Box>
                </AddPaymentButton>,
              );
            }
            actions.push(
              <>
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    gap: 16,
                  }}
                >
                  {`#${invoice.Id} ${dateUtils.shortDateFormat(
                    invoice.InvoiceStart,
                  )} - ${dateUtils.shortDateFormat(invoice.InvoiceEnd)}`}
                </Box>
                <ReactActions
                  id="addCharge_addPayment"
                  hoverMenu={true}
                  size="small"
                  items={subActions}
                />
              </>,
            );
          });
        }
        if (actions.length === 0) return <></>;
        return <ReactActions id={uniqueId()} items={actions} size="small" />;
      },
    ],
    [accumulateInvoices, isChargesAllowed, t],
  );
  return rowActions;
};

interface InvoiceDetailsLinkProps {
  invoiceId: number | null;
}
const InvoiceDetailsLink = ({ invoiceId }: InvoiceDetailsLinkProps) => {
  const { t } = useTranslation();
  if (invoiceId == null) {
    return invoiceId;
  }
  return (
    <InvoiceDetailsSidePanelLink id={invoiceId} useSidePanel={false}>
      <Tooltip
        title={t(
          translations.SeeFullDetailsOfBalanceAndTransferChargesToAnotherBudget,
        )}
        arrow
      >
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            gap: 16,
          }}
        >
          <Icon icon="file-alt" />
          {t(translations.BudgetDetailsBudgetTransfer)}
        </Box>
      </Tooltip>
    </InvoiceDetailsSidePanelLink>
    // </Link>
  );
};
function getPaymentAmount(invoice: IInvoiceBalanceDto) {
  const balance = invoice.Charges - invoice.Payments;
  if (balance > 0) {
    return balance;
  } else {
    return undefined;
  }
}

interface IRowWithFlatBudget {
  BudgetId: number | null;
  Budget: string | null;
}
const getBudget = (row: IRowWithFlatBudget) => {
  return toEntity(row.BudgetId, row.Budget);
};
interface IRowWithFlatServiceGroup {
  ServiceGroupId: number | null;
  ServiceGroup: string | null;
}
const getServiceGroup = (row: IRowWithFlatServiceGroup) => {
  return toEntity(row.ServiceGroupId, row.ServiceGroup);
};
